import { DataAction, Payload, Persistence, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Injectable } from '@angular/core';
import { Selector, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import * as moment from 'moment';
import { asyncScheduler, BehaviorSubject, combineLatest, Observable, of, SchedulerAction, lastValueFrom } from 'rxjs';
import { filter, first, skip, switchMap } from 'rxjs/operators';
import { EffectivePermissionCache, IEffectivePermissionCache, IEffectivePermissions, IPermissionType, PermissionType } from 'src/app/models/user';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { Utilities } from '../utilities';
import { RoleService } from './role.service';
import { UserEndpoint } from './user-endpoint.service';
import { UserService } from './user.service';
import { PermissionStateModel } from './user.state.models';
import { Mutex } from '../../shared/Mutex';

export enum PermissionDictionary {

    Cli = 'UI.Cli',
    Users = 'UI.Users',
    Configurations = 'UI.Configurations',

    Templates = 'UI.Templates',
    TemplateProofDownloadDocument = "UI.Templates.Proof.DownloadDocument",
    TemplateProofPrintDocument = "UI.Templates.Proof.PrintDocument",
    TemplateProofAnnotations = "UI.Templates.Proof.Annotations",
    TemplateDownloadDocument = "UI.Templates.DownloadDocument",

    ProcessViewAccess = 'ProcessView.Access',
    ProcessViewBlobAccess = 'ProcessView.BlobAccess',

    ProcessView = 'ProcessView',

    DocumentTypes = "UI.DocumentTypes",

    Orchestrations = 'UI.Orchestrations',

    Tenants = 'UI.Tenants',
    Tenant_SettingsEditor = 'UI.Tenants.Settings.Editor',

    // Government Relations
    GR = 'UI.GR',
    GR_OfficeFollowup = 'UI.GR.Office.Followups',
    Facilities = "UI.Facilities",

    eLynk_SmartFields_UseCache = "UI.eLynk.SmartFields.UseCache",
    eLynk_PdfTron_Document_Download = "UI.eLynk.PDFTron.Document.Download",
    eLynk_PdfTron_Annotations_Modify = "UI.eLynk.PDFTron.Annotations.Modify",
    eLynk_PdfTron_Annotations_Modify_QualityAssurance = "UI.eLynk.PDFTron.Annotations.Modify.QualityAssurance",
    elynk_Quality_MDCL = 'elynk.Quality.MDCL'
}

@Persistence([
    {
        path: 'permissionState.permissions',
        existingEngine: localStorage,
    }
])
@StateRepository()
@State<PermissionStateModel>({
    name: 'permissionState',
    defaults: {
        permissions: []
    }
})
@Injectable()
export class UserPermissionService extends NgxsDataRepository<PermissionStateModel> {

    getPermissionsByNameSpace(nameSpace: string): EffectivePermissionCache[] {
        return this.permissionsSync
            .filter(item => item.permission.name.startsWith(nameSpace))
            .filter(item => this.hasPermission(item.permission.name) == true)
    }

    getAllPermissionsByNameSpace(nameSpace: string): EffectivePermissionCache[] {
        return this.permissionsSync
            .filter(item => item.permission.name.startsWith(nameSpace));
    }

    get registrationExpiryMinutes() {
        return this.configurationService.permissionSettings?.registrationExpiryMinutes ?? 30;
    }

    get evaluationIntervalMinutes() {
        return this.configurationService.permissionSettings?.evaluationIntervalMinutes ?? 15;
    }

    private _permissionsChanged$ = new BehaviorSubject<Date>(null);
    permissionsChanged$ = this._permissionsChanged$.asObservable();

    @Selector() private static permissionsSelector(state: PermissionStateModel) {
        return state.permissions.map(o => new EffectivePermissionCache(o));
    }

    private get permissions$() {
        return this.store.select(UserPermissionService.permissionsSelector);
    }
    get permissionsSync() {
        return this.store.selectSnapshot(UserPermissionService.permissionsSelector);
    }

    readonly effectivePermissions: IEffectivePermissions;

    collectionMutex = new Mutex();

    constructor(
        private store: Store,
        private endpointService: UserEndpoint,
        private configurationService: ConfigurationService,
        private userService: UserService) {
        super();

        this.effectivePermissions = {};
    }

    ngxsAfterBootstrap(): void {

        // Listen to role changes. Avoid the first one which is the load from the local storage
        this.store.select(RoleService.currentRoleName).pipe(skip(1)).subscribe(async role => {
            await this.reEvaluateCachedPermissions();
        });

        this.buildPermissionObservables();

        this.initializeClearIdlePermissions();

    }

    async registerPermissions(permissionNames: (string | IPermissionType)[], exclusions: RegExp[] = null): Promise<IEffectivePermissionCache[]> {

        let permissionTypes = this.formatQueries(permissionNames);
        const newPermissions = await this.requestPermissionsWithExclusions(permissionTypes, exclusions);
        if (newPermissions.length > 0) {
            let changed: boolean = false;
            // update cached permissions
            const cachedPermissions = Utilities.cloneHard(this.permissionsSync);
            for (let permission of newPermissions) {
                const index = cachedPermissions.findIndex(cached => cached.permission.name == permission.permission.name);
                if (index >= 0) {
                    if (cachedPermissions[index].hasPermission != permission.hasPermission) {
                        cachedPermissions.splice(index, 1, permission);
                        changed = true;
                    } else {
                        cachedPermissions[index].lastRegistrationDate = permission.lastRegistrationDate;
                        cachedPermissions[index].ttlExpirationDate = permission.ttlExpirationDate;
                    }
                } else {
                    cachedPermissions.push(permission);
                    changed = true;
                }
            };

            this.cachePermissions(cachedPermissions, changed);

            const expiredPermissions = this.getAssociatedPermissions(permissionTypes).filter(cachedPermission => cachedPermission.hasExpiredTTL);
            this.removePermissions(expiredPermissions.map(item => item.permission.name));
        }

        return this.permissionsSync.filter(permission => permissionNames.some(name => name == permission.permission.name));

    }

    public hasPermission(name: string): boolean {
        for (let perm of this.permissionsSync) {
            if (perm?.permission?.name == name) {
                return perm?.hasPermission ?? false;
            }
        }

        return false;
    }

    private async requestPermissionsWithExclusions(permissionNames: (string | IPermissionType)[], exclusions: RegExp[] = null): Promise<IEffectivePermissionCache[]>
    {
        return this.requestPermissions(permissionNames, false, exclusions);
    }

    private async requestPermissions(permissionNames: (string | IPermissionType)[], force = false, exclusions: RegExp[] = null): Promise<IEffectivePermissionCache[]> {
        const unlock = await this.collectionMutex.lock();
        try {
            let permissionTypes = this.formatQueries(permissionNames);

            // filter by expired permissions
            permissionTypes = force ? permissionTypes : this.filterPermissionsToRequest(permissionTypes, exclusions);

            // request new version of the permissions from the backend
            let newPermissions: IEffectivePermissionCache[];
            if (permissionTypes.length > 0) {
                const currentDate = new Date();

                await lastValueFrom(this.userService.currentRole$.pipe(filter(_ => !!_), first()));
                newPermissions = (await this.endpointService.getPermissionStatuses(this.userService.currentRoleSync.objectId, permissionTypes))?.map(responseItem => {
                    responseItem.permission.ttl ??= 3600;
                    const result: IEffectivePermissionCache = new EffectivePermissionCache({
                        ...responseItem,
                        lastRegistrationDate: currentDate,
                        ttlExpirationDate: moment().add(responseItem.permission.ttl, 'seconds').toDate(),
                        registrationCount: 1
                    });

                    return result;
                });
            }

            return newPermissions ?? [];
        } finally {
            unlock();
        }
    }

    private formatQueries(permissionNames: (string | IPermissionType)[]) {
        return permissionNames.map(permission => {
            if (typeof permission === 'string') {
                const permissionName = permission;
                return ({
                    selectionName: permissionName,
                    permissionType: PermissionType.permission
                } as IPermissionType);
            } else {
                return permission;
            }
        });
    }

    private filterPermissionsToRequest(permissions: IPermissionType[], exclusions: RegExp[] = null): IPermissionType[] {
        const result: IPermissionType[] = [];
        permissions.forEach(reqPermission => {
            const desiredPermissionName = reqPermission.selectionName;
            if (reqPermission.permissionType == PermissionType.permission) {
                const cachedPermission = this.permissionsSync.find(cacheItem => cacheItem.permission.name == desiredPermissionName);

                if (!cachedPermission || cachedPermission.hasExpiredTTL) {
                    result.push(reqPermission);
                }
            } else {
                const cachedPermissions = this.permissionsSync.filter(cacheItem => !exclusions?.some(e => e.test(cacheItem.permission.name) ?? true)).filter(cacheItem => cacheItem.permission.name.startsWith(desiredPermissionName + '.'));

                if (cachedPermissions.length > 0) {

                    if (cachedPermissions.some(cachedPermission => cachedPermission.hasExpiredTTL)) {
                        result.push(reqPermission);
                    }
                }
                else {
                    result.push(reqPermission);
                }
            }
        });

        return result;
    }

    private getAssociatedPermissions(permissions: (string | IPermissionType)[]): IEffectivePermissionCache[] {
        let permissionTypes = this.formatQueries(permissions);
        return permissionTypes.reduce<IEffectivePermissionCache[]>((prev, cur) => {
            if (cur.permissionType == PermissionType.permission) {
                prev.push(...this.permissionsSync.filter(cacheItem => cacheItem.permission.name == cur.selectionName));
            } else {
                const desiredPermissionName = cur.selectionName;
                prev.push(...this.permissionsSync.filter(cacheItem => cacheItem.permission.name.startsWith(desiredPermissionName + '.')));
            }
            return prev;
        }, []);
    }

    private buildPermissionObservables() {
        const effectivePermissionNames = Object.getOwnPropertyNames(this.effectivePermissions);
        this.permissionsSync.forEach(cachedPermission => {
            if (effectivePermissionNames.every(effectiveName => effectiveName !== cachedPermission.permission.name)) {
                this.effectivePermissions[cachedPermission.permission.name] = this.hasAllPermissions$([cachedPermission.permission.name]);
            }
        });
    }

    private hasAllPermissions$(permissions: string[]): Observable<boolean> {
        const desiredPermissions: IPermissionType[] = [];
        permissions.forEach(permissionName => {
            desiredPermissions.push({ selectionName: permissionName, permissionType: PermissionType.permission } as IPermissionType);
        });
        return this.buildPermissionsListener$(desiredPermissions, (cached, desired) => desired.every(desiredItem => cached.some(cachedItem => cachedItem.permission.name === desiredItem.selectionName && cachedItem.hasPermission)));
    }

    private buildPermissionsListener$(desiredPermissions: IPermissionType[], acceptanceCondition: (effectivePermissions: IEffectivePermissionCache[], desiredPermissions: IPermissionType[]) => boolean): Observable<boolean> {
        return ((desiredPermissions: IPermissionType[], acceptanceCondition) => {
            return combineLatest([this.permissions$, of(desiredPermissions)]).pipe(switchMap(([cachedPermissions, desiredPermissions]) => {
                // update permissions
                const accept = acceptanceCondition(cachedPermissions, desiredPermissions);
                // track access
                return of(accept);
            }));
        })(desiredPermissions, acceptanceCondition);
    }

    @DataAction() private cachePermissions(@Payload('permissions') cachedPermissions: IEffectivePermissionCache[], @Payload('changed') changed: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), draft => {
            draft.permissions = cachedPermissions;
        }));

        this.buildPermissionObservables();

        if (changed) {
            this._permissionsChanged$.next(new Date());
        }
    }

    @DataAction() private removePermissions(@Payload('permissions') permissions: string[]) {

        const cachedPermissions = Utilities.cloneHard(this.permissionsSync);
        let updated = false;
        permissions.forEach(permission => {
            const index = cachedPermissions.findIndex(cached => cached.permission.name == permission);
            if (index >= 0) {
                cachedPermissions.splice(index, 1);
                updated = true;
            }
        });

        if (updated) {
            this.ctx.setState(produce(this.ctx.getState(), draft => {
                draft.permissions = cachedPermissions;
            }));
        }
    }

    private initializeClearIdlePermissions() {
        const clearIdleTask = (service: UserPermissionService) => {
            function result(this: SchedulerAction<any>, state) {

                // don't need to delete the idle permissions. The reloaExpired needs them to refresh their availability
                // service.clearIdlePermissions();
                service.reloadExpiredPermissions();
                this.schedule(null, service.evaluationIntervalMinutes * 60 * 1000);
            };

            return result;
        }

        asyncScheduler.schedule(clearIdleTask(this), 0, null);
    }

    // private async clearIdlePermissions(permissions: IEffectivePermissionCache[]) {
    //     // get cached permissions
    //     permissions ??= this.permissionsSync;

    //     const readyToRemove = permissions.filter(permission => this.readyToRemove(permission)).map(cached => cached.permission.name);

    //     this.removePermissions(readyToRemove);
    // }


    private readyToRemove(permission: IEffectivePermissionCache): boolean {
        const currentDate = new Date();
        return moment(currentDate).diff(permission.lastRegistrationDate, 'minutes', true) > this.registrationExpiryMinutes;
    }


    private async reloadExpiredPermissions() {
        // get  most recient ttl date
        const readyToReload = this.permissionsSync.filter(permission => permission.hasExpiredTTL).map(cached => cached.permission.name);

        const newPermissions = await this.requestPermissions(readyToReload);
        if (newPermissions.length > 0) {
            // update cached permissions
            const cachedPermissions = Utilities.cloneHard(this.permissionsSync);
            newPermissions.forEach(permission => {
                const index = cachedPermissions.findIndex(cached => cached.permission.name == permission.permission.name);
                if (index >= 0) {
                    cachedPermissions[index].hasPermission = permission.hasPermission;
                    cachedPermissions[index].ttlExpirationDate = permission.ttlExpirationDate;
                }
            });

            this.cachePermissions(cachedPermissions, true);
        }
    }

    private async reEvaluateCachedPermissions() {
        const permissionNames = this.permissionsSync.map(permission => permission.permission.name);
        const newPermissions = await this.requestPermissions(permissionNames, true);
        if (newPermissions.length > 0) {
            // update cached permissions
            const cachedPermissions = Utilities.cloneHard(this.permissionsSync);
            newPermissions.forEach(permission => {
                const index = cachedPermissions.findIndex(cached => cached.permission.name == permission.permission.name);
                if (index >= 0) {
                    cachedPermissions[index].hasPermission = permission.hasPermission;
                    cachedPermissions[index].ttlExpirationDate = permission.ttlExpirationDate;
                }
            });

            this.cachePermissions(cachedPermissions, true);
        }
    }

}

/**
 * Register permission decorator
 * @param permissions
 * @returns
 */



export function RequiredPermissions(permissions: (string | IPermissionType)[]) {
    return function _DecoratorName<T extends { new(...args: any[]): any }>(constructor: T) {
        return class extends constructor {
            constructor(...args: any[]) {
                super(...args);
                console.log('Args:', args);
                const userPermissionService = args.find(arg => !!(arg as UserPermissionService).registerPermissions) as UserPermissionService;
                if (!userPermissionService) {
                    throw 'UserPermissionService is not part of the constructor\'s arguments';
                }

                // Register permissions
                console.log('Registering permissions: 200', permissions);
                const effectivePermissions = userPermissionService.registerPermissions(permissions);
                // if (effectivePermissions.then) {
                //     effectivePermissions.then(value => {
                //         constructor.prototype.effectivePermissions = value;
                //     });
                // } else {
                //     constructor.prototype.effectivePermissions = effectivePermissions;
                // }
            }
        }
    }
}
